import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { throttle } from "lodash";

import { updateFirestoreDoc } from "./firebase";
import { arrayUnion } from "firebase/firestore";

import {
  SLIDE_ADDITIONAL_INFO,
  DISCUSS_PARTNER,
  DIRECTION,
  PLAYER_STATUS,
  SLIDES_BASE_URL,
  TRUSTED_MESSAGE_ORIGINS,
  INTERNAL_POST_MESSAGE_EVENT_NAMES,
} from "./constants";
import useSession from "./hooks/useSession";
import { playlistData } from "./playlistData";

import MainView from "./components/MainView";
import Controls from "./components/Controls";
import GlossaryModal from "./components/Glossary/GlossaryModal";

import useTutorialTooltips, {
  TUTORIAL_TOOLTIP_CONFIGS,
} from "./hooks/useTutorialTooltips";
import useTeachEventLogger, {
  TEACH_EVENT_LOG_TYPES,
} from "./hooks/useTeachEventLogger";
import useCtrlInputDispatcher from "./hooks/useCtrlInputDispatcher";
import useGameplayMessaging from "./hooks/useGameplayMessaging";
import useMsgAlerts, { MSG_ALERT_IDS, MsgAlert } from "./hooks/useMsgAlerts";

import "./App.css";

const { PREV, NEXT } = DIRECTION;
const {
  FREEPLAY_EVENT,
  LOGIN_EVENT,
  MESSAGING_READY,
  INIT_CTRL_INPUT_TYPE_DISPATCHING_EVENT,
  CTRL_INPUT_TYPE_DISPATCH_EVENT,
} = INTERNAL_POST_MESSAGE_EVENT_NAMES;
const {
  GAMEPLAY_PUZZLE_MOVE_INPUT_CLICK,
  GAMEPLAY_PUZZLE_MOVE_INPUT_KEYDOWN,
  SLIDE_NAV_NEXT,
  SLIDE_NAV_PREV,
} = TEACH_EVENT_LOG_TYPES;
const {
  SLIDE_SYNC,
  JUMP_SLIDE_ONE,
  JUMP_SLIDE_GAMEPLAY,
  LAST_SLIDE_CONTINUE_PLAY,
  JUMPED_EARLIER_PLAY_SLIDE,
} = MSG_ALERT_IDS;

// Slide Attributes
const LOGIN = "login";
const PLAY_TIME = "playtime";
const POLL_TIME = "polltime";
const POLL_RESULTS = "pollresults";
const LETS_DISCUSS = "letsdiscuss";
const FREEPLAY_SLIDE_ID = "playtime-keepgoing";
const VIDEO = "video";

const {
  pollresults: { label: pollresultsLabel },
  playtime: { label: playtimeLabel },
} = SLIDE_ADDITIONAL_INFO;

const isDiscussSlideAttribute = (attribute) =>
  [DISCUSS_PARTNER, LETS_DISCUSS].includes(attribute);

// session ids for public previews (see playlistData)
const DEFAULT_SESSION_ID =
  process.env.REACT_APP_DEPLOY === "production"
    ? "xrtXQCLoPCvzxhCQGaR1"
    : process.env.REACT_APP_DEPLOY === "qa"
    ? "NOWpcY9Uzku3EJJWjTgM"
    : "f1AnhqfJJBTYLawPxNWP";

const App = () => {
  const oneClickRef = useRef();
  const gameRef = useRef();
  const [slideUrl, setSlideUrl] = useState(null);
  const [pollResultsId, setPollResultsId] = useState(null);
  const [videoKey, setVideoKey] = useState(null);
  const [playtimePhase, setPlaytimePhase] = useState(null);
  const [discussPhase, setDiscussPhase] = useState(null);
  const [isGamePlaying, setIsGamePlaying] = useState(false);
  const [isSlidesLoading, setIsSlidesLoading] = useState(true);
  // stores callback handler when jumping to freeplay slide is done, also acts
  // a way to determine if currently in freeplay mode
  const [onFreeplayHandler, setOnFreeplayHandler] = useState(null);
  const [slidesAttributes, setSlidesAttributes] = useState([]);
  const [slidesSections, setSlidesSections] = useState([]);
  const [slidesMarks, setSlidesMarks] = useState([]);
  const [slideNumsToHideSlideDisplay, setSlideNumsToHideSlideDisplay] =
    useState([]);
  // used to detect session id changes to prevent running processSlideAttributes with the outdated slide attributes
  const [loadedSessionId, setLoadedSessionId] = useState(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isShowingGlossary, setIsShowingGlossary] = useState(false);

  const [disableArrowControls, setDisableArrowControls] = useState(false);
  const [timerModalInfo, setTimerModalInfo] = useState({
    isVisible: false,
    endTimerEarlyDirection: null,
  });

  const setCtrlInputDispatchConfig = useCtrlInputDispatcher();

  const [setIsGameplayMessagingReady, setGameplayLoginData] =
    useGameplayMessaging(gameRef, playtimePhase);

  const { sessionId = DEFAULT_SESSION_ID } = useParams();
  const {
    isPreview,
    currSlideNumber,
    isClassPause,
    lessonId,
    pollQuestions,
    pollResults,
    sessionStatsId,
    setCurrSlideNumber,
    slideChangeInfo,
    updateSlideChangeInfo,
    playerStatus,
    currPollNumCompleted,
    customizationNumCompleted,
    loggedInNum,
    setPauseAndClearPrevPoll,
    closePollAndShowResults,
    openPoll,
  } = useSession(sessionId);

  const throttledUpdateActiveExpirationTsFn = useMemo(
    () =>
      sessionId && !isPreview
        ? throttle(() => {
            const now = new Date();
            const newExpirationTs = new Date(
              now.setMinutes(now.getMinutes() + 30)
            );
            updateFirestoreDoc("sessions", sessionId, {
              activeExpirationTs: newExpirationTs,
            });
          }, 5 * 60 * 1000)
        : () => {},
    [isPreview, sessionId]
  );

  const [teachSessionUUID] = useState(uuidv4());

  const { currMsgAlertData, enqueueMsgAlert, popCurrMsgAlert } = useMsgAlerts(
    !!loadedSessionId,
    currSlideNumber
  );

  /** @todo refactor teacher stats (w/ platform?!) when more complete scope known */
  useEffect(() => {
    if (sessionStatsId) {
      updateFirestoreDoc("sessionStats", sessionStatsId, {
        pauses: arrayUnion({ isClassPause, timestamp: new Date() }),
      });
    }
  }, [sessionStatsId, isClassPause]);

  const thisPlaylistData = useMemo(
    () => playlistData?.[lessonId]?.data ?? {},
    [lessonId]
  );

  /**
   * set slideUrl only once and append FREEPLAY_SLIDE_ID to base slide url
   * to jump directly to freeplay slide if onFreeplayHandler is truthy
   * note: jumping directly to playtime-keepgoing minimizes back and forth slide
   * syncing between remote and slides.com which can sometimes flash the remote
   * synced slide before changing slide to playtime-keepgoing
   */
  /**
   * @todo update to fix possible race condition where if onFreeplayHandler is set
   * after thisPlaylistData.data becomes truthy, then jump to freeplay behavior
   * wont behave correctly, which can also have cascading effects where
   * isSlidesLoading to stay stuck as true */
  useEffect(() => {
    if (thisPlaylistData.slides && !slideUrl) {
      setSlideUrl(
        `${thisPlaylistData.slides}${
          onFreeplayHandler ? `/#${FREEPLAY_SLIDE_ID}` : ""
        }`
      );
    }
  }, [slideUrl, onFreeplayHandler, thisPlaylistData.slides]);

  // note slideNum is zero-based indexed
  const maybeGetSlideId = useCallback(
    (slideNum) => slidesAttributes[slideNum]?.id,
    [slidesAttributes]
  );
  const lessonNum = thisPlaylistData?.lessonNum;
  const currSlideId = maybeGetSlideId(currSlideNumber);
  const numOfSlides = slidesAttributes.length;

  const tutorialEventTriggerFlags = useMemo(
    () => ({
      isGamePlaying,
      areStudentsPlaying: playerStatus === PLAYER_STATUS.PLAYING,
    }),
    [isGamePlaying, playerStatus]
  );
  const [
    activeTutorialId,
    isTutorialTooltipBlockingInputs,
    completeActiveTutorial,
  ] = useTutorialTooltips(
    lessonNum,
    currSlideId,
    timerModalInfo.isVisible,
    [DISCUSS_PARTNER, PLAY_TIME].includes(parseSlideId(currSlideId).attribute),
    tutorialEventTriggerFlags
  );

  const dispatchTeachEventLog = useTeachEventLogger({
    teachSessionUUID,
    sessionId,
    lessonNum,
    currSlideNumber: currSlideNumber + 1,
    slideProgress: parseFloat((currSlideNumber / numOfSlides).toFixed(4)),
    isPreview,
    slideId: currSlideId,
    currSection: slidesAttributes[currSlideNumber]?.currSection
  });

  const shouldHideSlideDisplay = useCallback(
    (slideLabel) =>
      [
        pollresultsLabel,
        ...(thisPlaylistData.gameplay ? [playtimeLabel] : []),
      ].includes(slideLabel),
    [thisPlaylistData.gameplay]
  );

  const ctrlBarData = useMemo(() => {
    const ctrlBarReturnData = {
      isOnFirstSlide: !!slidesAttributes.length && currSlideNumber <= 0,
      isOnLastSlide:
        !!slidesAttributes.length &&
        currSlideNumber >= slidesAttributes.length - 1,
    };
    const ctrlBarSectionKeys = [PREV, "curr", NEXT];

    for (
      let i = currSlideNumber - 1, sectionIndex = 0;
      i <= currSlideNumber + 1;
      i++, sectionIndex++
    ) {
      // use one-based indexing for displayed slide numbers
      const currDisplaySlideNumber = i + 1;
      const sectionKey = ctrlBarSectionKeys[sectionIndex];
      if (i >= 0 && i < slidesAttributes.length) {
        ctrlBarReturnData[sectionKey] = {
          slideNumber: currDisplaySlideNumber,
          label: slidesAttributes[i]?.label,
          color: slidesAttributes[i]?.color,
          nextLabel: slidesAttributes[i]?.nextLabel,
        };
      } else {
        ctrlBarReturnData[sectionKey] = {
          slideNumber: currDisplaySlideNumber,
        };
      }
    }

    return ctrlBarReturnData;
  }, [slidesAttributes, currSlideNumber]);

  const handleSlidePostMessage = (method, args) => {
    document
      .getElementById("slide-deck")
      .contentWindow.postMessage(JSON.stringify({ method, args }), "*");
  };

  const goToSlideWithId = useCallback(
    (slideId) => {
      const slideIndex = slidesAttributes.findIndex(
        (slideData) => slideData.id === slideId
      );
      if (slideIndex >= 0) {
        handleSlidePostMessage("slide", [slideIndex]);
      }
    },
    [slidesAttributes]
  );

  const startTimerModalCallback = useCallback(() => {
    setPauseAndClearPrevPoll(false);
  }, [setPauseAndClearPrevPoll]);

  const processSlideAttribute = useCallback(
    (slideData) => {
      const { attribute } = slideData;

      let newPollResultsId = null;
      let newPlaytimePhase = null;
      let newDiscussPhase = null;
      let newVideoKey = null;

      if (attribute === PLAY_TIME) {
        setPauseAndClearPrevPoll(true);
        if (thisPlaylistData.gameplay?.[slideData.playtimePhase]) {
          newPlaytimePhase = slideData.playtimePhase;
        }
      } else if (attribute === POLL_TIME) {
        openPoll(pollResults, slideData.pollId);
      } else if (attribute === POLL_RESULTS) {
        closePollAndShowResults(slideData.pollId);
        newPollResultsId = slideData.pollId;
      } else if (isDiscussSlideAttribute(attribute)) {
        setPauseAndClearPrevPoll(slideData.discussId);
        // only DISCUSS_PARTNER should set discussPhase
        // because LETS_DISCUSS does not have a discussion timer
        if (attribute === DISCUSS_PARTNER) {
          newDiscussPhase = attribute;
        }
      } else if (attribute === VIDEO && thisPlaylistData?.video) {
        newVideoKey = slideData.videoKey;
      } else {
        setPauseAndClearPrevPoll(true);
      }

      setPollResultsId(newPollResultsId);
      setDiscussPhase(newDiscussPhase);
      setVideoKey(newVideoKey);
      if (thisPlaylistData?.gameplay) {
        setPlaytimePhase(newPlaytimePhase);
      }
    },
    [
      pollResults,
      thisPlaylistData,
      setPauseAndClearPrevPoll,
      closePollAndShowResults,
      openPoll,
    ]
  );

  const handleMessage = useCallback(
    (event) => {
      const { origin, data } = event;
      if (!TRUSTED_MESSAGE_ORIGINS[origin]) {
        return;
      }

      const eventData = JSON.parse(data);
      const { eventName, ...baseEventData } = eventData;
      if (origin === SLIDES_BASE_URL) {
        const { method } = eventData;
        if (eventName === "ready") {
          // initial slide attributes setup
          handleSlidePostMessage("getSlidesAttributes");

          // default to zero (i.e. first slide) if slideChangeInfo is not a number
          const zeroIndexedNewSlideNum =
            typeof slideChangeInfo.newSlideNumber === "number"
              ? slideChangeInfo.newSlideNumber - 1
              : 0;

          if (zeroIndexedNewSlideNum) {
            // sync with other remotes if necessary
            if (zeroIndexedNewSlideNum !== eventData.state.indexh) {
              handleSlidePostMessage("slide", [zeroIndexedNewSlideNum]);
            }
            // send alert message that they are being synced with a non first slide
            enqueueMsgAlert(SLIDE_SYNC, zeroIndexedNewSlideNum, {
              onBtnClickHandler: () => {
                handleSlidePostMessage("slide", [0]);
                enqueueMsgAlert(JUMP_SLIDE_ONE, 0);
              },
            });
          } else if (!onFreeplayHandler) {
            // slide is done loading if on first slide and not freeplay
            setIsSlidesLoading(false);
          }
        } else if (eventName === "slidechanged") {
          const { indexh } = eventData.state;
          const zeroIndexedSlide = slideChangeInfo.newSlideNumber - 1;

          // slide change initiated by slides, not by slidechangeinfo
          /*
            Note this is only necessary if slide changes _can_ be initiated
            by the slides.com embed.
            This has not always been the case,
            and this is not necessarily what we want.
            For now, for soft launch (March 2025),
            this code is necessary because the teacher does have access
            to the slides.com embed.
          */
          if (indexh !== zeroIndexedSlide) {
            const oneIndexedSlide = indexh + 1;
            updateSlideChangeInfo({
              hideSlideDisplay:
                slideNumsToHideSlideDisplay.includes(oneIndexedSlide) ||
                shouldHideSlideDisplay(ctrlBarData.curr.label),
              prevHiderSlideLabel:
                shouldHideSlideDisplay(ctrlBarData.curr.label) &&
                ctrlBarData.curr.label,
              newSlideNumber: oneIndexedSlide,
            });
            // Note one-click is not in focus and slide nav event is determiend
            // by calculating the delta of slides and one-click's zero-based slide num
            dispatchTeachEventLog(getSlideNavEvent(indexh - zeroIndexedSlide));
          }

          throttledUpdateActiveExpirationTsFn();
          // (always) set slide number state
          setCurrSlideNumber(indexh);
          // setting loading to false is necessary here if not freeplay and it
          // wasn't set in "ready"(because it needed to sync with remote)
          if (!onFreeplayHandler) {
            setIsSlidesLoading(false);
          }
        } else if (eventName === "callback") {
          const { result } = eventData;

          if (method === "getSlidesAttributes") {
            const {
              slides,
              sections,
              slidesMarks,
              slideNumsToHideSlideDisplay,
            } = addInfoToSlideAndGetSections(result, shouldHideSlideDisplay);
            setSlidesAttributes(slides);
            setSlidesSections(sections);
            setSlidesMarks(slidesMarks);
            setSlideNumsToHideSlideDisplay(slideNumsToHideSlideDisplay);
            setLoadedSessionId(sessionId);
          }
        }
      }
      // handle internal messages from either parent window or child iframe
      else if (eventName === LOGIN_EVENT) {
        setGameplayLoginData(baseEventData);
      } else if (eventName === MESSAGING_READY) {
        setIsGameplayMessagingReady(true);
      } else if (eventName === INIT_CTRL_INPUT_TYPE_DISPATCHING_EVENT) {
        setCtrlInputDispatchConfig({
          ...baseEventData,
          sourceWindow: event.source,
          sourceOrigin: origin,
        });
      } else if (eventName === CTRL_INPUT_TYPE_DISPATCH_EVENT) {
        const { inputType, isPuzzleMoveCtrl } = eventData;
        const isClick = inputType === "click";
        if (isPuzzleMoveCtrl && (isClick || inputType === "keydown")) {
          dispatchTeachEventLog(
            isClick
              ? GAMEPLAY_PUZZLE_MOVE_INPUT_CLICK
              : GAMEPLAY_PUZZLE_MOVE_INPUT_KEYDOWN
          );
        }
        /** @todo forward messaging to parent window if INIT_CTRL_INPUT_TYPE_DISPATCHING_EVENT was received? */
        // (could maybe update useCtrlInputDispatcher to listen for a custom event and handle forwarding to parent window)
      } else if (eventName === FREEPLAY_EVENT) {
        setOnFreeplayHandler(
          () => (slideNumber) =>
            void enqueueMsgAlert(JUMP_SLIDE_GAMEPLAY, slideNumber)
        );
      }
    },
    [
      ctrlBarData.curr.label,
      sessionId,
      setCurrSlideNumber,
      updateSlideChangeInfo,
      shouldHideSlideDisplay,
      slideChangeInfo,
      slideNumsToHideSlideDisplay,
      dispatchTeachEventLog,
      setCtrlInputDispatchConfig,
      setIsGameplayMessagingReady,
      setGameplayLoginData,
      enqueueMsgAlert,
      onFreeplayHandler,
      throttledUpdateActiveExpirationTsFn,
    ]
  );

  const handleNavigation = useCallback(
    (direction) => {
      const currSlideNumber = ctrlBarData.curr.slideNumber;
      const newSlideNumber =
        direction === PREV && !ctrlBarData.isOnFirstSlide
          ? currSlideNumber - 1
          : direction === NEXT && !ctrlBarData.isOnLastSlide
          ? currSlideNumber + 1
          : currSlideNumber;

      /**
       * hide slide display if direction's slide is poll results, playtime or
       * keep hidden (until slide is ready) if current slide was
       * poll results or playtime
       */
      updateSlideChangeInfo({
        hideSlideDisplay:
          shouldHideSlideDisplay(ctrlBarData[direction].label) ||
          shouldHideSlideDisplay(ctrlBarData.curr.label),
        prevHiderSlideLabel:
          shouldHideSlideDisplay(ctrlBarData.curr.label) &&
          ctrlBarData.curr.label,
        newSlideNumber,
      });

      // only logs if one-click is in focus (and not when slides.com is in focus)
      if (newSlideNumber !== currSlideNumber) {
        dispatchTeachEventLog(getSlideNavEvent(direction));
      }
    },
    [
      updateSlideChangeInfo,
      ctrlBarData,
      shouldHideSlideDisplay,
      dispatchTeachEventLog,
    ]
  );

  const handleGameplayClick = useCallback(
    (e) => {
      if (
        isGamePlaying &&
        !["game-pause-btn", "mmss"].includes(e.target.id) &&
        !isTutorialTooltipBlockingInputs
      ) {
        e.preventDefault();
        gameRef.current.contentWindow.focus();
      } else {
        window.focus();
      }
    },
    [isGamePlaying, isTutorialTooltipBlockingInputs]
  );

  const handleFullscreenToggle = useCallback(() => {
    if (isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        // Safari <16.4
        document.webkitExitFullscreen();
      }
    } else {
      if (oneClickRef.current.requestFullscreen) {
        oneClickRef.current.requestFullscreen();
      } else if (oneClickRef.current.webkitRequestFullscreen) {
        oneClickRef.current.webkitRequestFullscreen();
      }
    }
  }, [isFullscreen]);

  const handleGlossaryToggle = useCallback(() => {
    setIsShowingGlossary((prev) => !prev);
    if (activeTutorialId === TUTORIAL_TOOLTIP_CONFIGS.GLOSSARY.id) {
      completeActiveTutorial();
    }
  }, [activeTutorialId, completeActiveTutorial]);

  useEffect(() => {
    /*
    Prevent calling a post message twice for the same slide change if prev slide was just showing poll results or playtime
    and state got updated to unhide slide display which occurs in the useEffect below that listens for pollResultsId and playtimePhase changes
    */
    if (
      !slideChangeInfo.hideSlideDisplay &&
      slideChangeInfo.prevHiderSlideLabel
    ) {
      return;
    }
    if (slideChangeInfo.newSlideNumber) {
      handleSlidePostMessage("slide", [slideChangeInfo.newSlideNumber - 1]);
    }
  }, [slideChangeInfo]);

  useEffect(() => {
    const stateByLabels = {
      [pollresultsLabel]: pollResultsId,
      [playtimeLabel]: playtimePhase,
      video: videoKey,
    };
    const prevHiderSlideLabel = slideChangeInfo.prevHiderSlideLabel;
    // poll results or playtime became null and was previously showing, new slide
    // is ready so unhide display if current slide is not playtime or poll results
    if (!stateByLabels[prevHiderSlideLabel] && prevHiderSlideLabel) {
      updateSlideChangeInfo({
        hideSlideDisplay: !!stateByLabels[ctrlBarData.curr.label],
      });
    }
  }, [
    pollResultsId,
    playtimePhase,
    videoKey,
    updateSlideChangeInfo,
    slideChangeInfo.prevHiderSlideLabel,
    ctrlBarData.curr.label,
  ]);

  useEffect(() => {
    if (onFreeplayHandler && currSlideId === FREEPLAY_SLIDE_ID) {
      setIsSlidesLoading(false);
      // handles enqueuing msg alerts based on freeplay scenario
      onFreeplayHandler(currSlideNumber);
    }
  }, [currSlideId, currSlideNumber, enqueueMsgAlert, onFreeplayHandler]);

  // end freeplay when user navigates away from freeplay gameplay slide
  useEffect(() => {
    if (!isSlidesLoading && currSlideId !== FREEPLAY_SLIDE_ID) {
      setOnFreeplayHandler(null);
    }
  }, [currSlideId, isSlidesLoading]);

  useEffect(() => {
    if (ctrlBarData.isOnLastSlide) {
      const currSlideNumber = ctrlBarData.curr.slideNumber - 1;
      enqueueMsgAlert(LAST_SLIDE_CONTINUE_PLAY, currSlideNumber, {
        onBtnClickHandler: () => {
          setOnFreeplayHandler(
            () => (slideNumber) =>
              void enqueueMsgAlert(JUMPED_EARLIER_PLAY_SLIDE, slideNumber, {
                onBtnClickHandler: () => {
                  handleSlidePostMessage("slide", [currSlideNumber]);
                },
              })
          );
          goToSlideWithId(FREEPLAY_SLIDE_ID);
        },
      });
    }
  }, [ctrlBarData, enqueueMsgAlert, goToSlideWithId]);

  /** @todo only process currSlideId after slides are synced with remote (for L1, gameplay flashes before synced slide change) */
  useEffect(() => {
    if (slidesAttributes.length > 0) {
      processSlideAttribute(parseSlideId(currSlideId));
    }
  }, [slidesAttributes, processSlideAttribute, currSlideId]);

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);

  useEffect(() => {
    if (window.location !== window.parent.location) {
      window.parent.postMessage(
        JSON.stringify({ eventName: MESSAGING_READY }),
        // NOTE: window.location.ancestorOrigins is not supported by firefox ATOW so
        // document.referrer is kept as a fallback (https://bugzilla.mozilla.org/show_bug.cgi?id=1085214)
        window.location.ancestorOrigins?.[0] || document.referrer
      );
    }
    /**
     * NOTE Fullscreen API without vendor specific prefix (i.e. webkit) is not
     * widely available across all browsers (i.e. Safari <16.4) so a check on
     * the element's fullscreen request method is necessary to determine
     * which event listener to add
     *
     * listener on fullscreen change is necessary for updating fullscreen toggle
     * button icon since users can exit fullscreen without using the button and
     * instead use 'ESC' or browser specific UI (i.e. the message browsers
     * display when entering fullscreen)
     */
    if (oneClickRef.current.requestFullscreen) {
      const listener = () => setIsFullscreen(!!document.fullscreenElement);

      window.addEventListener("fullscreenchange", listener);

      return () => {
        window.removeEventListener("fullscreenchange", listener);
      };
    } else if (oneClickRef.current.webkitRequestFullscreen) {
      const listener = () =>
        setIsFullscreen(!!document.webkitFullscreenElement);

      window.addEventListener("webkitfullscreenchange", listener);

      return () => {
        window.removeEventListener("webkitfullscreenchange", listener);
      };
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleGameplayClick);
    return () => {
      document.removeEventListener("click", handleGameplayClick);
    };
  }, [handleGameplayClick]);

  /**
   * NOTE isGamePlaying should only be true if playtimePhase is truthy so
   * resetting isGamePlaying to false is necessary if state and UI gets out
   * of sync which is known to happen when activating teacher play and
   * immediately pressing left/right arrow key casuing a race condition with
   * Gameplay click event and slides.come keydown event
   */
  useEffect(() => {
    if (!playtimePhase && isGamePlaying) {
      setIsGamePlaying(false);
    }
  }, [isGamePlaying, playtimePhase]);

  return (
    <div id="one-click" ref={oneClickRef}>
      <MsgAlert {...currMsgAlertData} popCurrMsgAlert={popCurrMsgAlert} />
      {isPreview && <div id="watermark-preview">Preview</div>}
      <MainView
        hideSlideDisplay={slideChangeInfo.hideSlideDisplay}
        pollQuestion={pollQuestions[pollResultsId]}
        pollResults={pollResults[pollResultsId]}
        isSlidesLoading={isSlidesLoading}
        slideUrl={slideUrl}
        handleSlidePostMessage={handleSlidePostMessage}
        numOfStudentsData={
          !isSlidesLoading &&
          maybeGetNumOfStudentsData(
            currSlideId,
            loggedInNum,
            currPollNumCompleted
          )
        }
        gameRef={gameRef}
        playtimePhase={playtimePhase}
        discussPhase={discussPhase}
        gameplayRoute={thisPlaylistData?.gameplay?.[playtimePhase]}
        isGamePlaying={isGamePlaying}
        setIsGamePlaying={setIsGamePlaying}
        videoId={thisPlaylistData?.video?.[videoKey]}
        timerTimes={thisPlaylistData?.timerTimes}
        setDisableArrowControls={setDisableArrowControls}
        timerModalInfo={timerModalInfo}
        setTimerModalInfo={setTimerModalInfo}
        handleNavigation={handleNavigation}
        nextSlideLabel={ctrlBarData.next.label}
        startTimerModalCallback={startTimerModalCallback}
        isShowingGlossary={isShowingGlossary}
        isTutorialTooltipBlockingInputs={isTutorialTooltipBlockingInputs}
        completeActiveTutorial={completeActiveTutorial}
        customizationNumCompleted={customizationNumCompleted}
        lessonNum={lessonNum}
        isOnFirstSlide={ctrlBarData.isOnFirstSlide}
        dispatchTeachEventLog={dispatchTeachEventLog}
        setOnFreeplayHandler={setOnFreeplayHandler}
        onFreeplayHandler={onFreeplayHandler}
      />
      <Controls
        disableArrowControls={disableArrowControls}
        ctrlBarData={ctrlBarData}
        updateSlideChangeInfo={updateSlideChangeInfo}
        numOfSlides={numOfSlides}
        isFullscreen={isFullscreen}
        isShowingGlossary={isShowingGlossary}
        handleFullscreenToggle={handleFullscreenToggle}
        handleGlossaryToggle={handleGlossaryToggle}
        playerStatus={playerStatus}
        slideSections={slidesSections}
        slidesMarks={slidesMarks}
        slideNumsToHideSlideDisplay={slideNumsToHideSlideDisplay}
        thumbnailImages={thisPlaylistData?.thumbnail}
        isShowingVideo={!!videoKey}
        isGamePlaying={isGamePlaying}
        setIsGamePlaying={setIsGamePlaying}
        playtimePhase={playtimePhase}
        shouldHideSlideDisplay={shouldHideSlideDisplay}
        timerModalInfo={timerModalInfo}
        setTimerModalInfo={setTimerModalInfo}
        handleNavigation={handleNavigation}
        activeTutorialId={activeTutorialId}
        isTutorialTooltipBlockingInputs={isTutorialTooltipBlockingInputs}
        completeActiveTutorial={completeActiveTutorial}
        isSessionLoaded={!!loadedSessionId}
        dispatchTeachEventLog={dispatchTeachEventLog}
        maybeGetSlideId={maybeGetSlideId}
      />
      <GlossaryModal
        isVisible={isShowingGlossary}
        handleGlossaryToggle={handleGlossaryToggle}
        lessonNum={lessonNum ?? 1}
        dispatchTeachEventLog={dispatchTeachEventLog}
      />
    </div>
  );
};

export default App;

// utility to return slide attribute and unique id which can be a pollId or playtimeId
// note: assumes pollId is kebab-case
function parseSlideId(slideId) {
  const slideData = {};
  const match = slideId?.match(/^(.*?)-(.*?)$/);

  if (match) {
    slideData.attribute = match[1];

    if ([POLL_TIME, POLL_RESULTS].includes(match[1])) {
      slideData.pollId = match[2];
    } else if (isDiscussSlideAttribute(match[1])) {
      slideData.discussId = slideId;
    } else if (PLAY_TIME === match[1]) {
      slideData.playtimePhase = match[2];
    } else if (VIDEO === match[1]) {
      slideData.videoKey = match[2];
    }
  } else {
    slideData.attribute = slideId;
  }

  return slideData;
}

function getColorAndLabelObj(slideId) {
  if (slideId?.match(/(poll|playtime|letsdiscuss|discusspartner).*/)) {
    const { attribute } = parseSlideId(slideId);
    return (
      SLIDE_ADDITIONAL_INFO[attribute] ?? {
        label: null,
        sectionColor: null,
        nextLabel: null,
        notch: false,
      }
    );
  }

  return SLIDE_ADDITIONAL_INFO[slideId] ?? { label: null, sectionColor: null };
}

function addInfoToSlideAndGetSections(slides, shouldHideSlideDisplay) {
  const sections = [];
  const slidesMarks = [];
  const slideNumsToHideSlideDisplay = [];
  let currColor = "";
  let currSection = "";
  for (let i = 0; i < slides.length; i++) {
    const { label, nextLabel, sectionColor, notch } = getColorAndLabelObj(
      slides[i]?.id
    );

    // update color for this section
    if (sectionColor) {
      currColor = sectionColor;

      // if section color, means start of new section
      sections.push({
        color: currColor,
        cummulativeSectionLength: i / (slides.length - 1),
      });

      currSection = slides[i].id;
    }

    if (notch) {
      // add 1 to value to match one-based indexing for displayed slide numbers
      slidesMarks.push({
        value: i + 1,
        color: currColor,
        text: label,
      });
    }

    if (shouldHideSlideDisplay(label)) {
      // add 1 to value to match one-based indexing
      slideNumsToHideSlideDisplay.push(i + 1);
    }

    slides[i].color = currColor;
    slides[i].label = label;
    slides[i].nextLabel = nextLabel;
    slides[i].currSection = currSection;
  }
  return { slides, sections, slidesMarks, slideNumsToHideSlideDisplay };
}

function maybeGetNumOfStudentsData(
  currSlideId,
  loggedInNum,
  currPollNumCompleted
) {
  const { attribute } = parseSlideId(currSlideId);

  if (attribute === LOGIN) {
    return {
      type: "logged-in",
      number: loggedInNum,
    };
  } else if (attribute === POLL_TIME) {
    return {
      type: "curr-poll-completed",
      number: currPollNumCompleted,
    };
  } else {
    return null;
  }
}

function getSlideNavEvent(directionOrDelta) {
  if ([1, NEXT].includes(directionOrDelta)) {
    return SLIDE_NAV_NEXT;
  } else if ([-1, PREV].includes(directionOrDelta)) {
    return SLIDE_NAV_PREV;
  }
}
